<template>
	<div class="kanban-column">
		<div class="column-container elevation-2 round-card background-2">
			<!-- Column Header -->
			<div class="column-header pa-3" :style="{ borderTopColor: column.color }">
				<div class="d-flex align-center justify-space-between">
					<div class="d-flex align-center">
						<v-icon small :color="column.color" class="mr-2">{{ column.icon }}</v-icon>
						<span class="column-title font-weight-medium">{{ column.label }}</span>
					</div>
					<div class="d-flex align-center">
						<v-btn
							icon
							x-small
							class="mr-1"
							:title="allSelected ? 'Deseleziona tutti i task' : 'Seleziona tutti i task'"
							:disabled="!localTasks.length"
							@click="$emit('select-all', localTasks)"
						>
							<v-icon small>{{ allSelected ? 'mdi-checkbox-multiple-marked' : 'mdi-checkbox-multiple-outline' }}</v-icon>
						</v-btn>
						<v-chip small :color="column.color" dark>{{ localTasks.length }}</v-chip>
					</div>
				</div>
			</div>

			<v-divider></v-divider>

			<!-- Column Content -->
			<div class="column-content background-1 pa-2">
				<draggable
					v-model="localTasks"
					:group="{ name: 'tasks-status' }"
					:animation="200"
					ghost-class="ghost"
					chosen-class="chosen"
					@add="onItemAdded"
					class="draggable-container"
				>
					<task
						v-for="task in localTasks"
						:key="task.id"
						:task="task"
						:selects-mode="selectsMode"
					/>
				</draggable>

				<!-- Empty state -->
				<div v-if="!localTasks.length && !showInlineInput" class="empty-state text-center py-4">
					<v-icon color="grey lighten-1" size="32">mdi-checkbox-blank-circle-outline</v-icon>
					<div class="text-caption grey--text mt-2">Nessun task</div>
				</div>
			</div>

			<v-divider></v-divider>

			<!-- Add Task Footer -->
			<div class="column-footer pa-2 background-2">
				<!-- Input inline per creazione rapida -->
				<div v-if="showInlineInput">
					<!-- Selezione tasklist se ce ne sono multiple -->
					<v-select
						v-if="tasklists.length > 1"
						v-model="selectedTasklistId"
						:items="tasklists"
						item-text="text"
						item-value="value"
						label="Tasklist"
						dense
						outlined
						hide-details
						class="mb-2"
					></v-select>

					<v-text-field
						v-model="newTaskTitle"
						placeholder="Titolo task..."
						dense
						outlined
						hide-details
						autofocus
						:loading="savingQuick"
						@keyup.enter="quickAddTask"
						@keyup.esc="cancelQuickAdd"
					>
						<template v-slot:append>
							<v-btn icon small :loading="savingQuick" :disabled="!canSave" @click="quickAddTask">
								<v-icon>check</v-icon>
							</v-btn>
							<v-btn icon small @click="cancelQuickAdd">
								<v-icon>close</v-icon>
							</v-btn>
						</template>
					</v-text-field>
				</div>

				<!-- Pulsante creazione inline -->
				<v-btn
					v-else
					text
					small
					style="text-transform: none !important;"
					@click="startInlineAdd"
					:disabled="!tasklists.length"
				>
					<v-icon left small>add</v-icon>
					Aggiungi Task
				</v-btn>
			</div>
		</div>
	</div>
</template>

<script>
import Draggable from 'vuedraggable'
import Task from '@/components/common/Task'

export default {
	name: 'TaskboardKanbanColumn',

	components: {
		Draggable,
		Task
	},

	props: {
		column: {
			type: Object,
			required: true
		},
		selectsMode: {
			type: Boolean,
			default: false
		},
		tasklists: {
			type: Array,
			default: () => []
		}
	},

	data() {
		return {
			localTasks: [],
			showInlineInput: false,
			newTaskTitle: '',
			savingQuick: false,
			selectedTasklistId: null
		}
	},

	computed: {
		canSave() {
			return this.newTaskTitle.trim() && this.selectedTasklistId
		},

		allSelected() {
			return this.localTasks.length > 0 && this.localTasks.every(t => t.selected)
		}
	},

	watch: {
		'column.tasks': {
			immediate: true,
			deep: true,
			handler(newVal) {
				this.localTasks = newVal ? [...newVal] : []
			}
		},
		tasklists: {
			immediate: true,
			handler(newVal) {
				// Seleziona automaticamente la prima tasklist se ce n'è solo una
				if (newVal && newVal.length === 1) {
					this.selectedTasklistId = newVal[0].value
				}
			}
		}
	},

	methods: {
		onItemAdded(evt) {
			// This fires on the DESTINATION column when an item is dropped here
			const movedTask = this.localTasks[evt.newIndex]
			if (movedTask) {
				// Aggiorna lo status del task localmente per l'update ottimistico
				movedTask.status = this.column.status

				this.$emit('status-changed', {
					taskId: movedTask.id,
					newStatus: this.column.status,
					task: movedTask
				})
			}

			// Sincronizza i task locali con il parent
			this.$emit('tasks-updated', {
				status: this.column.status,
				tasks: [...this.localTasks]
			})
		},

		startInlineAdd() {
			this.showInlineInput = true
			// Seleziona la prima tasklist se non già selezionata
			if (!this.selectedTasklistId && this.tasklists.length > 0) {
				this.selectedTasklistId = this.tasklists[0].value
			}
		},

		async quickAddTask() {
			if (!this.canSave) return

			this.savingQuick = true
			try {
				const response = await this.axios.post('tasks/', {
					title: this.newTaskTitle.trim(),
					tasklist_id: this.selectedTasklistId,
					status: this.column.status,
					priority: 'c' // Default: media
				})

				// Aggiungi il task alla lista locale
				if (response.data && response.data.data) {
					this.localTasks.push(response.data.data)
					// Notifica il parent
					this.$emit('task-added', response.data.data)
				}

				this.newTaskTitle = ''
				this.showInlineInput = false

				// Notifica successo
				this.$eventBusUtility.setSnackbar(response, this.$store)
			} catch (error) {
				this.$eventBusUtility.$emit('errorHandler', error)
			} finally {
				this.savingQuick = false
			}
		},

		cancelQuickAdd() {
			this.newTaskTitle = ''
			this.showInlineInput = false
		},

		// Method to add task when dragged from another column
		addTask(task) {
			this.localTasks.unshift(task)
		},

		// Method to remove task when dragged to another column
		removeTask(taskId) {
			const index = this.localTasks.findIndex(t => t.id === taskId)
			if (index > -1) {
				this.localTasks.splice(index, 1)
			}
		}
	}
}
</script>

<style scoped>
.kanban-column {
	width: 344px;
	min-width: 344px;
	max-width: 344px;
	margin-right: 24px;
	height: 100%;
}

.column-container {
	display: flex;
	flex-direction: column;
	max-height: 100%;
	border-top: 4px solid;
	border-top-color: inherit;
}

.column-header {
	border-top: 4px solid;
}

.column-content {
	flex: 1;
	overflow-y: auto;
	min-height: 100px;
	max-height: calc(100vh - 300px);
}

.column-footer {
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;
}

.draggable-container {
	min-height: 50px;
}

.ghost {
	opacity: 0.5;
	background: #c8ebfb;
}

.chosen {
	box-shadow: 0 8px 16px rgba(0,0,0,0.2);
}

.empty-state {
	opacity: 0.6;
}
</style>
